import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MIGRATION_MAP_FEATURES } from '../data/map-features';
import {
  MIGRATION_ROUTE_GROUPS,
  type MigrationRouteGroup,
} from '../data/migration-routes';
import {
  getSeasonalState,
} from '../data/migration-seasonality';
import {
  BLOOM_MAP_PINS,
  type BloomMapCategory,
} from '../data/bloom-map-pins';
import { TIDE_STATIONS, fetchTideHighLow } from '../data/tide-stations';
import { GLOBAL_TIDE_REFS } from '../data/global-tide-refs';
import { fetchObservationsInBounds } from '../data/inaturalist';
import {
  seismicColorForMagnitude,
  seismicMagnitudeBarPercent,
  SEISMIC_GRADIENT_CSS,
  SEISMIC_MAG_MAX,
  SEISMIC_MAG_MIN,
} from '../data/seismic-scale';
import {
  fetchSeismicMarkers,
  ACTIVE_VOLCANOES,
} from '../data/volcanoes';

const DARK_TILE =
  'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
const TILE_ATTRIB = '&copy; OSM &copy; CARTO';

export const ALL_MIGRATION_IDS = MIGRATION_MAP_FEATURES.map((f) => f.id);

const COL = {
  migration: '#e8b4bc',
  trees: '#7dcea0',
  flowers: '#c4b5d4',
  crops: '#deb887',
  tide: '#5dade2',
  tideGlobal: '#85c1e9',
  inat: '#f4d03f',
  you: '#af7ac5',
  volcano: '#e74c3c',
};

const VOLCANO_FALLBACK_IMAGE_URL = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 360">
    <defs>
      <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#d4e2ff"/>
        <stop offset="100%" stop-color="#95b3e8"/>
      </linearGradient>
      <linearGradient id="ground" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#9a7757"/>
        <stop offset="100%" stop-color="#6e523b"/>
      </linearGradient>
    </defs>
    <rect x="0" y="0" width="640" height="220" fill="url(#sky)"/>
    <rect x="0" y="220" width="640" height="140" fill="url(#ground)"/>
    <polygon points="165,255 320,65 475,255" fill="#4f3b2d"/>
    <polygon points="250,255 320,160 390,255" fill="#7e5d46" opacity="0.7"/>
    <text x="320" y="315" text-anchor="middle" font-family="Arial, sans-serif" font-size="19" fill="#f6f6f6">Volcanic landscape</text>
  </svg>`
)}`;

const VOLCANO_WIKI_TITLES: Record<string, string> = {
  'Kīlauea': 'Kilauea',
  'Mauna Loa': 'Mauna Loa',
  'Pico do Fogo': 'Pico do Fogo',
  Vesuvius: 'Mount Vesuvius',
  Etna: 'Mount Etna',
  Sakurajima: 'Sakurajima',
  Fuji: 'Mount Fuji',
  Merapi: 'Mount Merapi',
  Agung: 'Mount Agung',
  Krakatau: 'Krakatoa',
  Taal: 'Taal Volcano',
  Mayon: 'Mayon',
  Pinatubo: 'Mount Pinatubo',
  Colima: 'Volcano of Colima',
  Popocatépetl: 'Popocatepetl',
  Fuego: 'Volcán de Fuego',
  'Nevado del Ruiz': 'Nevado del Ruiz',
  Cotopaxi: 'Cotopaxi',
  Sabancaya: 'Sabancaya',
  Calbuco: 'Calbuco',
  'White Island': 'Whakaari / White Island',
  Whakaari: 'Whakaari / White Island',
  Eyjafjallajökull: 'Eyjafjallajökull',
  Grímsvötn: 'Grímsvötn',
  Bárðarbunga: 'Bárðarbunga',
  Klyuchevskoy: 'Klyuchevskaya Sopka',
  Bezymianny: 'Bezymianny',
  'St. Helens': 'Mount St. Helens',
  'Mount Hood': 'Mount Hood',
  'Clear Lake Volcanic Field': 'Clear Lake Volcanic Field',
  Augustine: 'Augustine Volcano',
  Novarupta: 'Novarupta',
  Cleveland: 'Mount Cleveland (Alaska)',
  Nyiragongo: 'Mount Nyiragongo',
  'Ol Doinyo Lengai': 'Ol Doinyo Lengai',
};

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

function esc(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

function hexToRgba(hex: string, opacity: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${opacity})`;
}

export type MigrationPanelPayload = { groupId: string };

export interface WorldMapState {
  migrationMasterOn: boolean;
  migrationIds: Set<string>;
  migrationGroupIds: Set<string>;
  bloomTrees: boolean;
  bloomFlowers: boolean;
  bloomCrops: boolean;
  tidesNoaa: boolean;
  tidesGlobal: boolean;
  inat: boolean;
  volcanoes: boolean;
}

export type WorldMapApi = {
  getState: () => WorldMapState;
  setMigrationMaster: (on: boolean) => void;
  setMigration: (id: string, on: boolean) => void;
  setMigrationGroup: (id: string, on: boolean) => void;
  setBloomCategory: (cat: BloomMapCategory, on: boolean) => void;
  setTidesNoaa: (on: boolean) => void;
  setTidesGlobal: (on: boolean) => void;
  setInat: (on: boolean) => void;
  setVolcanoes: (on: boolean) => void;
  setUserLocation: (lat: number | null, lng: number | null) => void;
  flyTo: (lat: number, lng: number, zoom?: number) => void;
  invalidateSize: () => void;
  destroy: () => void;
};

export function initWorldMap(
  el: HTMLElement,
  options?: {
    migrationIds?: Set<string>;
    migrationGroupIds?: Set<string>;
    savedLocation?: { lat: number; lng: number } | null;
    onMigrationPanel?: (p: MigrationPanelPayload) => void;
  }
): WorldMapApi {
  const onMigrationPanel = options?.onMigrationPanel;
  const state: WorldMapState = {
    migrationMasterOn: true,
    bloomTrees: true,
    bloomFlowers: true,
    bloomCrops: true,
    tidesNoaa: false,
    tidesGlobal: false,
    inat: false,
    volcanoes: true,
    migrationIds:
      options?.migrationIds != null
        ? new Set(options.migrationIds)
        : new Set(ALL_MIGRATION_IDS),
    migrationGroupIds:
      options?.migrationGroupIds != null
        ? new Set(options.migrationGroupIds)
        : new Set(
            MIGRATION_ROUTE_GROUPS.filter(
              (g) => getSeasonalState(g.id).visuallyActive
            ).map((g) => g.id)
          ),
  };

  let userLat: number | null = options?.savedLocation?.lat ?? null;
  let userLng: number | null = options?.savedLocation?.lng ?? null;

  const startCenter: [number, number] =
    userLat != null && userLng != null
      ? [userLat, userLng]
      : [22, 10];
  const startZoom =
    userLat != null && userLng != null ? 7 : 2;

  const map = L.map(el, {
    worldCopyJump: true,
    minZoom: 2,
    maxZoom: 11,
    scrollWheelZoom: true,
  }).setView(startCenter, startZoom);

  L.tileLayer(DARK_TILE, {
    attribution: TILE_ATTRIB,
    subdomains: 'abcd',
    maxZoom: 20,
  }).addTo(map);

  const baseLayer = L.layerGroup().addTo(map);
  const inatLayer = L.layerGroup().addTo(map);
  const volcanoLayer = L.layerGroup().addTo(map);
  const userLayer = L.layerGroup().addTo(map);

  let inatAbort: AbortController | null = null;
  let volcanoAbort: AbortController | null = null;
  let inatDebounce: ReturnType<typeof setTimeout> | null = null;

  function addBaseMarker(
    lat: number,
    lng: number,
    title: string,
    desc: string,
    color: string,
    onPopupOpen?: (root: HTMLElement) => void
  ): void {
    const cm = L.circleMarker([lat, lng], {
      radius: 7,
      fillColor: color,
      color: 'rgba(255,240,245,0.55)',
      weight: 2,
      opacity: 1,
      fillOpacity: 0.88,
    });
    if (onPopupOpen) {
      cm.bindPopup('<div class="tide-popup-root">Loading…</div>', {
        maxWidth: 280,
      });
      cm.on('popupopen', () => {
        setTimeout(() => {
          const p = cm
            .getPopup()
            ?.getElement()
            ?.querySelector('.tide-popup-root');
          if (p instanceof HTMLElement) onPopupOpen(p);
        }, 0);
      });
    } else {
      cm.bindPopup(
        `<strong>${esc(title)}</strong><br/><span style="opacity:0.9;font-size:0.88em">${esc(desc)}</span>`
      );
    }
    cm.addTo(baseLayer);
  }

  function catmullRom(
    p0: number,
    p1: number,
    p2: number,
    p3: number,
    t: number
  ): number {
    // Catmull-Rom spline (uniform parameterization). Used for visual smoothing.
    return (
      0.5 *
      (2 * p1 +
        (-p0 + p2) * t +
        (2 * p0 - 5 * p1 + 4 * p2 - p3) * t * t +
        (-p0 + 3 * p1 - 3 * p2 + p3) * t * t * t)
    );
  }

  /**
   * Keep each longitude within 180° of the previous point so spline sampling
   * does not interpolate the long way across the map (antimeridian crossing).
   * Leaflet accepts lng outside [-180, 180]; coordinates stay on one continuous path.
   */
  function unwrapWaypointLongitudes(
    waypoints: [number, number][]
  ): [number, number][] {
    if (waypoints.length === 0) return [];
    const out: [number, number][] = [[waypoints[0][0], waypoints[0][1]]];
    for (let i = 1; i < waypoints.length; i++) {
      const [lat, lng] = waypoints[i];
      const prevLng = out[i - 1][1];
      let delta = lng - prevLng;
      while (delta > 180) delta -= 360;
      while (delta < -180) delta += 360;
      out.push([lat, prevLng + delta]);
    }
    return out;
  }

  function sampleSmoothedRoutePoints(
    waypoints: [number, number][],
    samplesPerSegment: number
  ): [number, number][] {
    waypoints = unwrapWaypointLongitudes(waypoints);
    if (waypoints.length < 2) return waypoints;
    const segCount = waypoints.length - 1;
    const out: [number, number][] = [];

    for (let seg = 0; seg < segCount; seg++) {
      const p0 = seg - 1 >= 0 ? waypoints[seg - 1] : waypoints[seg];
      const p1 = waypoints[seg];
      const p2 = waypoints[seg + 1];
      const p3 =
        seg + 2 < waypoints.length ? waypoints[seg + 2] : waypoints[seg + 1];

      for (let j = 0; j < samplesPerSegment; j++) {
        const t = j / samplesPerSegment;
        const lat = catmullRom(p0[0], p1[0], p2[0], p3[0], t);
        const lng = catmullRom(p0[1], p1[1], p2[1], p3[1], t);
        out.push([lat, lng]);
      }
    }

    out.push(waypoints[waypoints.length - 1]);
    return out;
  }

  function addBloomAreaMarker(
    lat: number,
    lng: number,
    title: string,
    desc: string,
    color: string
  ): void {
    // Filled area (not dots) to represent where blooms are found.
    const radiusMeters = 35000; // ~35 km
    const circle = L.circle([lat, lng], {
      radius: radiusMeters,
      fillColor: color,
      color: 'rgba(0,0,0,0)',
      weight: 0,
      opacity: 0.2,
      fillOpacity: 0.22,
      interactive: true,
    });
    circle.bindPopup(
      `<strong>${esc(title)}</strong><br/><span style="opacity:0.9;font-size:0.88em">${esc(desc)}</span>`
    );
    circle.addTo(baseLayer);
  }

  function drawMigrationRoute(group: MigrationRouteGroup): void {
    const pts = group.waypoints;
    if (pts.length < 2) return;
    const seasonal = getSeasonalState(group.id);
    const w = group.lineWeight ?? 3;
    const splinePts = sampleSmoothedRoutePoints(
      pts,
      // More samples = smoother curve.
      Math.max(10, Math.min(22, pts.length * 4))
    );
    const nSeg = splinePts.length - 1;

    for (let i = 0; i < nSeg; i++) {
      const t = nSeg > 1 ? i / (nSeg - 1) : 1;
      const segOp = 0.15 + 0.65 * t;
      L.polyline([splinePts[i], splinePts[i + 1]], {
        color: hexToRgba(group.color, segOp),
        weight: w,
        opacity: 1,
        interactive: false,
      }).addTo(baseLayer);
    }

    const dashLine = L.polyline(splinePts as L.LatLngExpression[], {
      color: group.color,
      weight: Math.max(1, w - 1),
      opacity: 0.92,
      dashArray: '10 18',
      interactive: false,
    });
    dashLine.addTo(baseLayer);

    const hitLine = L.polyline(splinePts as L.LatLngExpression[], {
      color: '#000',
      weight: Math.max(22, w * 5),
      opacity: 0.01,
      interactive: true,
    });
    hitLine.addTo(baseLayer);
    hitLine.on('click', (e) => {
      L.DomEvent.stopPropagation(e);
      onMigrationPanel?.({ groupId: group.id });
    });

    requestAnimationFrame(() => {
      const path = (dashLine as unknown as { _path?: SVGPathElement })._path;
      if (!path) return;
      path.classList.add('mig-dash-path');
      if (seasonal.dashReversed) path.classList.add('mig-dash-rev');
      else path.classList.add('mig-dash-fwd');
      if (seasonal.activity === 'peak') {
        path.classList.add('mig-dash-glow');
      }
    });

    if (
      seasonal.progress !== null &&
      seasonal.progress >= 0 &&
      seasonal.progress <= 1
    ) {
      const tt = Math.max(0, Math.min(1, seasonal.progress));
      const idx = tt * (splinePts.length - 1);
      const i = Math.min(Math.floor(idx), splinePts.length - 2);
      const u = idx - i;
      const [lat0, lng0] = splinePts[i];
      const [lat1, lng1] = splinePts[i + 1];
      const lat = lat0 + (lat1 - lat0) * u;
      const lng = lng0 + (lng1 - lng0) * u;
      const peakClass =
        seasonal.activity === 'peak' ? ' mig-now-circle--peak' : '';
      const nowM = L.circleMarker([lat, lng], {
        radius: 8,
        fillColor: group.color,
        color: 'rgba(255,255,255,0.55)',
        weight: 2,
        fillOpacity: 0.95,
        className: `mig-now-circle${peakClass}`,
      });
      nowM.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        onMigrationPanel?.({ groupId: group.id });
      });
      nowM.addTo(baseLayer);
    }
  }

  function redrawBase(): void {
    baseLayer.clearLayers();

    if (state.migrationMasterOn) {
      MIGRATION_ROUTE_GROUPS.filter(
        (g) =>
          state.migrationGroupIds.has(g.id) &&
          getSeasonalState(g.id).visuallyActive
      ).forEach(drawMigrationRoute);
    }

    MIGRATION_MAP_FEATURES.filter((f) => state.migrationIds.has(f.id)).forEach(
      (f) => {
        addBaseMarker(f.lat, f.lng, f.title, f.description, COL.migration);
      }
    );

    BLOOM_MAP_PINS.filter((b) => {
      if (b.category === 'trees' && !state.bloomTrees) return false;
      if (b.category === 'flowers' && !state.bloomFlowers) return false;
      if (b.category === 'crops' && !state.bloomCrops) return false;
      return true;
    }).forEach((b) => {
      addBloomAreaMarker(b.lat, b.lng, b.title, b.description, COL[b.category]);
    });

    if (state.tidesNoaa) {
      TIDE_STATIONS.forEach((st) => {
        addBaseMarker(
          st.lat,
          st.lng,
          `Tides (NOAA) — ${st.name}`,
          'Click for next high/low.',
          COL.tide,
          async (root) => {
            root.innerHTML = `<em>Loading…</em>`;
            const r = await fetchTideHighLow(st.noaaId);
            if ('error' in r) {
              root.innerHTML = `<p>${esc(r.error)}</p><a href="https://tidesandcurrents.noaa.gov/stationhome.html?id=${st.noaaId}" target="_blank" rel="noopener">NOAA →</a>`;
            } else {
              root.innerHTML = `<strong>${esc(st.name)}</strong>${r.html}`;
            }
          }
        );
      });
    }

    if (state.tidesGlobal) {
      GLOBAL_TIDE_REFS.forEach((g) => {
        L.circleMarker([g.lat, g.lng], {
          radius: 6,
          fillColor: COL.tideGlobal,
          color: 'rgba(255,255,255,0.45)',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.88,
        })
          .bindPopup(
            `<strong>${esc(g.name)}</strong> (${esc(g.region)})<br/><span style="font-size:0.82em;opacity:0.9">External tide chart.</span><br/><a href="${g.url.replace(/"/g, '%22')}" target="_blank" rel="noopener">Tide forecast →</a>`
          )
          .addTo(baseLayer);
      });
    }
  }

  function updateUserMarker(): void {
    userLayer.clearLayers();
    if (userLat == null || userLng == null) return;
    L.circleMarker([userLat, userLng], {
      radius: 11,
      fillColor: COL.you,
      color: '#fff',
      weight: 3,
      opacity: 1,
      fillOpacity: 0.9,
    })
      .bindPopup('<strong>You are here</strong><br/><span style="font-size:0.85em">Saved region for map & iNat</span>')
      .addTo(userLayer);
  }

  async function refreshInat(): Promise<void> {
    if (!state.inat) {
      inatLayer.clearLayers();
      return;
    }
    inatAbort?.abort();
    inatAbort = new AbortController();
    const signal = inatAbort.signal;
    let b = map.getBounds();
    let latSpan = Math.abs(b.getNorth() - b.getSouth());
    let lngSpan = Math.abs(b.getEast() - b.getWest());
    if (latSpan > 22 || lngSpan > 35) {
      const c = map.getCenter();
      const dLat = Math.min(11, Math.max(4, latSpan * 0.35));
      const dLng = Math.min(16, Math.max(6, lngSpan * 0.35));
      b = L.latLngBounds(
        [c.lat - dLat, c.lng - dLng],
        [c.lat + dLat, c.lng + dLng]
      );
      latSpan = Math.abs(b.getNorth() - b.getSouth());
    }
    try {
      const pins = await fetchObservationsInBounds(
        b.getNorth(),
        b.getEast(),
        b.getSouth(),
        b.getWest(),
        { signal, perPage: 55 }
      );
      if (signal.aborted) return;
      inatLayer.clearLayers();
      for (const p of pins) {
        L.circleMarker([p.lat, p.lng], {
          radius: 5,
          fillColor: COL.inat,
          color: 'rgba(0,0,0,0.35)',
          weight: 1,
          opacity: 1,
          fillOpacity: 0.85,
        })
          .bindPopup(
            `<strong>${esc(p.name)}</strong>${p.observedOn ? `<br/><span style="font-size:0.8em;opacity:0.85">${esc(p.observedOn)}</span>` : ''}<br/><a href="${p.url.replace(/"/g, '')}" target="_blank" rel="noopener">iNaturalist →</a>`
          )
          .addTo(inatLayer);
      }
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') return;
      inatLayer.clearLayers();
    }
  }

  function scheduleInat(): void {
    if (!state.inat) return;
    if (inatDebounce) clearTimeout(inatDebounce);
    inatDebounce = setTimeout(() => {
      inatDebounce = null;
      void refreshInat();
    }, 650);
  }

  function buildSeismicPopupHtml(
    mag: number,
    depthKm: number | null,
    timeMs: number | null,
    place: string,
    url: string
  ): string {
    const magPct = seismicMagnitudeBarPercent(mag);
    const magColor = seismicColorForMagnitude(mag);
    const depthLabel =
      depthKm != null ? `${depthKm.toFixed(1)} km` : 'Unknown';
    const depthPct =
      depthKm != null ? Math.round(clamp01(depthKm / 700) * 100) : 0;
    const whenLabel =
      timeMs != null
        ? new Date(timeMs).toLocaleString([], {
            dateStyle: 'medium',
            timeStyle: 'short',
          })
        : 'Unknown time';
    return `<div class="geo-popup">
      <strong style="color:${magColor}">M ${mag.toFixed(1)}</strong> — ${esc(place)}
      <div class="seismic-stats">
        <div class="seismic-stats__row">
          <span class="seismic-stats__label">Magnitude</span>
          <span class="seismic-stats__value" style="color:${magColor}">M ${mag.toFixed(1)}</span>
        </div>
        <div class="seismic-stats__bar seismic-stats__bar--mag" aria-hidden="true">
          <div class="seismic-stats__scale-track" style="background:${SEISMIC_GRADIENT_CSS}"></div>
          <span class="seismic-stats__scale-marker" style="left:${magPct}%;background:${magColor}"></span>
        </div>
        <div class="seismic-stats__scale-ends">
          <span>M${SEISMIC_MAG_MIN} light</span>
          <span>M${SEISMIC_MAG_MAX}+ major</span>
        </div>
        <div class="seismic-stats__row">
          <span class="seismic-stats__label">Depth</span>
          <span class="seismic-stats__value">${depthLabel}</span>
        </div>
        <div class="seismic-stats__bar seismic-stats__bar--depth" aria-hidden="true"><span style="width:${depthPct}%"></span></div>
      </div>
      <div class="geo-popup__caption">Event time: ${esc(whenLabel)}</div>
      <a href="${url.replace(/"/g, '')}" target="_blank" rel="noopener">USGS details →</a>
    </div>`;
  }

  function wikiSummaryUrl(pageTitle: string): string {
    return `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(pageTitle)}`;
  }

  function sanitizeAttr(s: string): string {
    return s.replace(/"/g, '&quot;');
  }

  async function fetchVolcanoImageUrl(
    name: string,
    signal: AbortSignal
  ): Promise<string> {
    const candidates = [
      VOLCANO_WIKI_TITLES[name],
      `${name} volcano`,
      name,
    ].filter((x): x is string => Boolean(x));

    for (const title of candidates) {
      try {
        const res = await fetch(wikiSummaryUrl(title), { signal });
        if (!res.ok) continue;
        const data = (await res.json()) as {
          thumbnail?: { source?: string };
          originalimage?: { source?: string };
        };
        const img = data.thumbnail?.source ?? data.originalimage?.source;
        if (img && /^https?:\/\//.test(img)) return img;
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') throw e;
      }
    }
    return VOLCANO_FALLBACK_IMAGE_URL;
  }

  function buildVolcanoPopupHtml(name: string, region: string, imageUrl: string): string {
    return `<div class="geo-popup">
      <strong>${esc(name)}</strong><br/><span style="opacity:0.9">${esc(region)}</span>
      <img class="geo-popup__media" src="${sanitizeAttr(imageUrl)}" alt="Photo of ${esc(name)} in ${esc(region)}." loading="lazy" onerror="this.onerror=null;this.src='${VOLCANO_FALLBACK_IMAGE_URL}'" />
      <div class="geo-popup__caption">Landscape photo from ${esc(region)}.</div>
    </div>`;
  }

  async function refreshVolcanoes(): Promise<void> {
    volcanoLayer.clearLayers();
    if (!state.volcanoes) return;
    volcanoAbort?.abort();
    volcanoAbort = new AbortController();
    const signal = volcanoAbort.signal;
    try {
      const seismic = await fetchSeismicMarkers({ signal });
      if (signal.aborted) return;
      for (const s of seismic) {
        L.circleMarker([s.lat, s.lng], {
          radius: 4 + Math.min(4, Math.max(0, s.mag)),
          fillColor: seismicColorForMagnitude(s.mag),
          color: 'rgba(0,0,0,0.35)',
          weight: 1,
          opacity: 1,
          fillOpacity: 0.92,
        })
          .bindPopup(
            buildSeismicPopupHtml(
              s.mag,
              s.depthKm,
              s.timeMs,
              s.place,
              s.url
            )
          )
          .addTo(volcanoLayer);
      }
      for (const v of ACTIVE_VOLCANOES) {
        const volcanoMarker = L.circleMarker([v.lat, v.lng], {
          radius: 6,
          fillColor: COL.volcano,
          color: '#000000',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.95,
        }).addTo(volcanoLayer);

        volcanoMarker.bindPopup('<div class="geo-popup-root">Loading volcano photo…</div>', {
          maxWidth: 280,
        });
        volcanoMarker.on('popupopen', () => {
          const popup = volcanoMarker.getPopup();
          const root = popup?.getElement()?.querySelector('.geo-popup-root');
          if (!(root instanceof HTMLElement)) return;
          const req = new AbortController();
          root.innerHTML = '<em>Loading volcano photo…</em>';
          void fetchVolcanoImageUrl(v.name, req.signal)
            .then((imageUrl) => {
              root.innerHTML = buildVolcanoPopupHtml(v.name, v.region, imageUrl);
            })
            .catch((e) => {
              if (e instanceof Error && e.name === 'AbortError') return;
              root.innerHTML = buildVolcanoPopupHtml(
                v.name,
                v.region,
                VOLCANO_FALLBACK_IMAGE_URL
              );
            });
          volcanoMarker.once('popupclose', () => req.abort());
        });
      }
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') return;
    }
  }

  map.on('moveend', scheduleInat);
  map.on('zoomend', scheduleInat);

  redrawBase();
  updateUserMarker();
  void refreshVolcanoes();

  const resize = () => map.invalidateSize();
  window.addEventListener('resize', resize);
  setTimeout(resize, 150);

  return {
    getState: () => ({
      migrationMasterOn: state.migrationMasterOn,
      migrationIds: new Set(state.migrationIds),
      migrationGroupIds: new Set(state.migrationGroupIds),
      bloomTrees: state.bloomTrees,
      bloomFlowers: state.bloomFlowers,
      bloomCrops: state.bloomCrops,
      tidesNoaa: state.tidesNoaa,
      tidesGlobal: state.tidesGlobal,
      inat: state.inat,
      volcanoes: state.volcanoes,
    }),
    setMigrationMaster(on: boolean) {
      state.migrationMasterOn = on;
      redrawBase();
    },
    setMigration(id: string, on: boolean) {
      if (on) state.migrationIds.add(id);
      else state.migrationIds.delete(id);
      redrawBase();
    },
    setMigrationGroup(id: string, on: boolean) {
      if (on) state.migrationGroupIds.add(id);
      else state.migrationGroupIds.delete(id);
      redrawBase();
    },
    setBloomCategory(cat: BloomMapCategory, on: boolean) {
      if (cat === 'trees') state.bloomTrees = on;
      if (cat === 'flowers') state.bloomFlowers = on;
      if (cat === 'crops') state.bloomCrops = on;
      redrawBase();
    },
    setTidesNoaa(on: boolean) {
      state.tidesNoaa = on;
      redrawBase();
    },
    setTidesGlobal(on: boolean) {
      state.tidesGlobal = on;
      redrawBase();
    },
    setInat(on: boolean) {
      state.inat = on;
      if (!on) {
        inatAbort?.abort();
        inatLayer.clearLayers();
      } else void refreshInat();
    },
    setVolcanoes(on: boolean) {
      state.volcanoes = on;
      if (!on) {
        volcanoAbort?.abort();
        volcanoLayer.clearLayers();
      } else void refreshVolcanoes();
    },
    setUserLocation(lat: number | null, lng: number | null) {
      userLat = lat;
      userLng = lng;
      updateUserMarker();
    },
    flyTo(lat: number, lng: number, zoom = 8) {
      map.setView([lat, lng], zoom);
    },
    invalidateSize: resize,
    destroy: () => {
      inatAbort?.abort();
      volcanoAbort?.abort();
      if (inatDebounce) clearTimeout(inatDebounce);
      window.removeEventListener('resize', resize);
      map.remove();
    },
  };
}
